import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Layout } from '@components';
import { usePrefersReducedMotion, useLanguage } from '@hooks';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { fetchPosts, fetchPostDetail, parseTags, formatDate } from '@utils/api';
import BlogCard from '@components/BlogCard';
import WechatcodeImg from '@images/Wechatcode.jpg';

// 语言环境映射
const getLocaleFromLanguage = language => {
  const localeMap = {
    en: 'en-US',
    'zh-CN': 'zh-CN',
    'zh-TW': 'zh-TW',
  };
  return localeMap[language] || 'en-US';
};

const CopyIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 1024 1024" fill="currentColor">
    <path
      d="M832 64H296c-4.4 0-8 3.6-8 8v56c0 4.4 3.6 8 8 8h496v688c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8V96c0-17.7-14.3-32-32-32zM704 192H192c-17.7 0-32 14.3-32 32v530.7c0 8.5 3.4 16.6 9.4 22.6l173.3 173.3c2.2 2.2 4.7 4 7.4 5.5v1.9h4.4c2 0.5 4.1 0.8 6.1 0.8H704c17.7 0 32-14.3 32-32V224c0-17.7-14.3-32-32-32zM350 856.2L263.9 770H350v86.2zM672 888H414V746c0-22.1-17.9-40-40-40H232V256h440v632z"
      fill="currentColor"
    />
  </svg>
);

// SVG图标组件 - 使用正确的图标文件内容
const XIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 1024 1024" fill="currentColor">
    <path
      d="M761.759375 122h132.320625L605 452.4003125 945.08 902H678.8L470.24 629.3196875 231.599375 902H99.2l309.1996875-353.4L82.16 122h273.0403125l188.52 249.24z m-46.4390625 700.8h73.32L315.359375 197.0403125h-78.680625z"
      fill="#ffffff"
    />
  </svg>
);

const XiaohongshuIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 1024 1024" fill="currentColor">
    <path
      d="M512 2.27555555a509.72444445 509.72444445 0 1 0 0 1019.4488889A509.72444445 509.72444445 0 0 0 512 2.27555555z"
      fill="#FF0035"
    />
    <path
      d="M651.37322667 388.93795555c3.64088889 0 5.53415111 0 5.53415111 5.6069689v46.38492444c0 3.64088889-1.82044445 5.53415111-5.53415111 5.53415111h-25.99594667c-5.53415111 0-7.42741333 1.82044445-7.42741333 7.42741333v161.29137778c0 3.71370667 1.89326222 5.60696889 5.60696888 5.60696889h46.38492445c5.53415111 0 7.35459555 1.82044445 7.35459555 7.42741333v44.41884445c0 5.60696889-1.82044445 7.50023111-7.35459555 7.50023111-29.70965333-1.89326222-61.16693333-1.89326222-92.76984889-1.89326222H482.58161778c-5.53415111 0-7.35459555 0-3.64088889-5.53415112 7.35459555-14.85482667 14.78200889-31.53009778 24.02986666-46.38492444 1.89326222-3.64088889 3.71370667-5.53415111 9.32067556-5.53415111h46.38492444c4.36906667 0 5.31569778-1.16508445 5.46133334-4.51470222V451.99815111c0-5.53415111-1.82044445-7.35459555-7.28177778-5.53415111h-25.99594666c-5.60696889 0-7.42741333-1.82044445-7.42741334-5.53415111v-44.56448c0-5.53415111 1.82044445-7.35459555 7.42741334-7.35459556h120.51342222zM391.77784889 617.14887111c12.96156445 5.53415111 27.81639111 3.64088889 42.5984 3.64088889h37.13706666c3.64088889 0 5.53415111 1.89326222 3.6408889 5.60696889-9.24785778 16.74808889-16.67527111 33.42336-25.9231289 50.09863111-1.82044445 3.64088889-3.64088889 3.64088889-7.42741333 3.64088889-9.24785778-1.82044445-20.38897778-1.82044445-35.24380444-1.82044444-11.06830222 0-27.81639111 1.89326222-40.77795556-1.82044445-5.53415111-1.89326222-7.42741333-3.71370667-3.64088889-7.42741333 9.17504-16.74808889 16.60245333-31.53009778 24.02986667-48.20536889 1.89326222-1.89326222 1.89326222-5.60696889 5.60696889-3.71370667z m393.07036444-244.81336889c3.71370667 0 5.60696889 1.89326222 5.60696889 5.60696889 0 12.96156445 0 12.96156445 12.96156445 12.96156444 16.74808889 0 31.53009778 3.71370667 44.49166222 11.14112 18.56853333 11.14112 25.99594667 29.63683555 25.99594666 50.02581334v37.13706666c0 5.53415111 1.82044445 7.42741333 7.42741334 7.42741334 20.38897778 0 37.06424889 5.53415111 50.02581333 22.20942222a55.55996445 55.55996445 0 0 1 11.14112 33.42336V626.32391111c0 27.81639111-16.74808889 48.20536889-44.49166222 51.91907556a75.73048889 75.73048889 0 0 1-35.24380445-1.82044445 48.05973333 48.05973333 0 0 1-33.35054222-42.67121777c0-5.53415111 1.82044445-3.64088889 5.53415112-3.6408889h46.38492444c5.53415111 0 7.42741333-1.89326222 7.42741333-7.5002311v-48.2053689c0-12.96156445-7.42741333-20.38897778-20.38897777-20.38897777h-70.4876089c-3.71370667 0-5.60696889 1.89326222-5.60696888 5.53415111 1.89326222 18.56853333 1.89326222 37.13706667 1.89326222 57.52604444v55.63278222c0 5.53415111-1.89326222 7.42741333-7.42741334 7.42741334h-44.49166222c-5.60696889 0-7.42741333-1.89326222-7.42741333-7.42741334V561.44327111c0-5.53415111-1.89326222-7.42741333-7.42741333-7.42741333h-42.67121778c-5.53415111 0-7.42741333 0-7.42741334-5.53415111v-46.38492445c0-5.53415111 1.89326222-5.53415111 7.42741334-5.53415111h44.49166222c5.60696889 0 7.42741333-1.89326222 5.60696889-7.42741333v-35.24380445c0-5.60696889-1.89326222-5.60696889-7.42741333-5.60696888h-25.99594667c-3.64088889 0-5.53415111 0-5.53415111-5.53415112v-46.38492444c0-3.64088889 1.89326222-5.53415111 5.53415111-5.53415111h25.99594667c3.64088889 0 5.53415111-1.82044445 5.53415111-5.53415111 0-11.35957333 0-12.81592889 9.97603555-12.96156445h41.94304z m-543.29344-5.53415111c5.53415111 0 7.42741333 1.82044445 7.42741334 7.42741334-1.45635555 34.73408-1.74762667 67.42926222-1.82044445 98.81372444V626.32391111a52.35598222 52.35598222 0 0 1-7.50023111 27.81639111c-9.24785778 18.56853333-25.92312889 24.10268445-46.31210666 22.28224a41.72458667 41.72458667 0 0 1-38.95751112-31.53009777c-5.60696889-18.56853333-5.60696889-16.74808889 12.96156445-18.56853334 7.42741333 0 16.74808889 3.71370667 22.28224-1.82044444 3.64088889-3.71370667 1.82044445-12.96156445 1.82044444-20.38897778V374.15594667c0-5.60696889 1.89326222-7.42741333 7.42741333-7.42741334h42.67121778zM148.78492445 451.99815111c-3.64088889 40.77795555-5.53415111 81.55591111-11.06830223 122.40668444a160.92728889 160.92728889 0 0 1-29.70965333 74.20131556c1.89326222 0 0 1.82044445 0 3.64088889-11.14112-20.38897778-20.38897778-40.77795555-31.53009778-61.16693333-1.82044445-1.82044445-1.82044445-3.64088889 0-5.53415112 7.42741333-16.74808889 7.42741333-33.35054222 7.42741334-51.91907555l5.60696888-81.55591111c0-3.78652445 1.82044445-5.60696889 5.53415112-5.60696889h48.20536888c3.71370667 0 5.53415111 0 5.53415112 5.53415111zM337.96551111 446.464c5.53415111 0 5.53415111 1.82044445 7.42741334 7.42741333 1.82044445 37.13706667 5.53415111 74.20131555 9.24785777 111.26556445 0 3.64088889 0 9.24785778 1.89326223 12.96156444 3.64088889 7.42741333 1.82044445 14.85482667-1.89326223 22.28224-7.42741333 14.78200889-14.78200889 31.53009778-22.20942222 46.31210667-1.89326222 5.60696889-3.71370667 5.60696889-7.42741333 0-12.96156445-20.38897778-20.38897778-40.77795555-25.99594667-64.80782222C293.54666667 559.47719111 293.54666667 535.37450667 291.58058667 513.16508445c-1.82044445-20.38897778-3.64088889-38.95751111-5.53415112-59.3464889 0-5.60696889 0-7.42741333 7.42741334-7.42741333 7.42741333 1.82044445 14.85482667 0 22.20942222 0h22.28224z m144.61610667-74.20131555c5.60696889 0 5.60696889 1.89326222 3.71370667 5.60696888l-33.35054223 66.77390222c-1.82044445 1.82044445-1.82044445 3.64088889-3.71370667 7.35459556-1.82044445 3.71370667 0 5.60696889 3.71370667 7.42741334 3.64088889 1.89326222 3.64088889-1.82044445 5.53415111-3.6408889 1.89326222-3.78652445 3.71370667-5.60696889 9.32067556-5.60696888h44.41884444c5.60696889 0 5.60696889 1.82044445 3.78652445 5.53415111-14.85482667 27.81639111-27.81639111 57.52604445-42.67121778 85.34243555-5.53415111 11.14112-3.64088889 14.85482667 9.24785778 14.85482667 3.71370667-1.89326222 11.14112-1.89326222 18.56853333-1.89326222-7.42741333 12.96156445-12.96156445 25.99594667-18.56853333 38.95751111-3.64088889 9.24785778-9.24785778 11.14112-16.67527111 11.14112h-42.5984c-3.71370667 0-7.42741333 0-11.14112-1.89326222-13.03438222-3.64088889-16.74808889-12.96156445-13.03438222-25.92312889 7.42741333-20.38897778 18.56853333-38.95751111 27.8163911-59.34648889 1.89326222-3.71370667 3.71370667-5.60696889 5.6069689-11.14112h-37.13706667c-14.78200889-3.71370667-20.38897778-12.96156445-14.78200889-27.81639111 5.53415111-16.74808889 14.85482667-33.35054222 22.20942222-48.20536889 9.32067555-16.74808889 16.74808889-35.24380445 25.99594667-51.91907556 1.82044445-3.71370667 3.64088889-5.60696889 9.24785777-5.60696888h44.49166223z m315.30097777 74.20131555c-3.71370667 0-5.60696889 1.82044445-5.60696888 5.53415111v38.95751111c0 3.71370667 1.89326222 5.60696889 5.60696888 5.60696889h20.38897778c3.64088889 0 5.53415111-1.89326222 5.53415112-5.60696889v-30.58346667c-0.21845333-13.90819555-2.76707555-13.90819555-25.9231289-13.90819555z m126.04757334-57.52604445c13.03438222 1.89326222 24.10268445 14.85482667 24.10268444 29.70965334s-11.06830222 25.99594667-25.92312888 27.81639111h-14.85482667c-18.49571555 0-18.49571555 0-18.49571556-18.56853333 0-5.53415111 0-12.96156445 1.82044445-18.49571556 3.71370667-14.85482667 16.74808889-22.28224 33.35054222-20.38897778z"
      fill="#FFFFFF"
    />
  </svg>
);

const TelegramIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 1024 1024" fill="currentColor">
    <path
      d="M883.626667 155.946667s82.858667-32.341333 75.946666 46.165333c-2.261333 32.298667-22.997333 145.408-39.125333 267.776l-55.253333 362.453333s-4.608 53.077333-46.08 62.293334c-41.386667 9.258667-103.552-32.298667-115.072-41.514667-9.216-6.912-172.672-110.805333-230.272-161.621333-16.085333-13.824-34.517333-41.557333 2.346666-73.856l241.749334-230.826667c27.605333-27.733333 55.253333-92.373333-59.861334-13.866667l-322.346666 219.306667s-36.864 23.04-105.898667 2.304l-149.674667-46.165333s-55.253333-34.645333 39.125334-69.248C349.482667 370.56 632.661333 259.84 883.626667 155.904z"
      fill="#ffffff"
    />
  </svg>
);

const WeixinIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 1024 1024" fill="currentColor">
    <path
      d="M664.250054 368.541681c10.015098 0 19.892049 0.732687 29.67281 1.795902-26.647917-122.810047-159.358451-214.077703-310.826188-214.077703-169.353083 0-308.085774 114.232694-308.085774 259.274068 0 83.708494 46.165436 152.460344 123.281791 205.78483l-30.80868 91.730191 107.688651-53.455469c38.558178 7.53665 69.459978 15.308661 107.924012 15.308661 9.66308 0 19.230993-0.470721 28.752858-1.225921-6.025227-20.36584-9.521864-41.723264-9.521864-63.862493C402.328693 476.632491 517.908058 368.541681 664.250054 368.541681zM498.62897 285.87389c23.200398 0 38.557154 15.120372 38.557154 38.061874 0 22.846334-15.356756 38.156018-38.557154 38.156018-23.107277 0-46.260603-15.309684-46.260603-38.156018C452.368366 300.994262 475.522716 285.87389 498.62897 285.87389zM283.016307 362.090758c-23.107277 0-46.402843-15.309684-46.402843-38.156018 0-22.941502 23.295566-38.061874 46.402843-38.061874 23.081695 0 38.46301 15.120372 38.46301 38.061874C321.479317 346.782098 306.098002 362.090758 283.016307 362.090758zM945.448458 606.151333c0-121.888048-123.258255-221.236753-261.683954-221.236753-146.57838 0-262.015505 99.348706-262.015505 221.236753 0 122.06508 115.437126 221.200938 262.015505 221.200938 30.66644 0 61.617359-7.609305 92.423993-15.262612l84.513836 45.786813-23.178909-76.17082C899.379213 735.776599 945.448458 674.90216 945.448458 606.151333zM598.803483 567.994292c-15.332197 0-30.807656-15.096836-30.807656-30.501688 0-15.190981 15.47546-30.477129 30.807656-30.477129 23.295566 0 38.558178 15.286148 38.558178 30.477129C637.361661 552.897456 622.099049 567.994292 598.803483 567.994292zM768.25071 567.994292c-15.213493 0-30.594809-15.096836-30.594809-30.501688 0-15.190981 15.381315-30.477129 30.594809-30.477129 23.107277 0 38.558178 15.286148 38.558178 30.477129C806.808888 552.897456 791.357987 567.994292 768.25071 567.994292z"
      fill="#ffffff"
    />
  </svg>
);

const MailIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 1024 1024" fill="currentColor">
    <path
      d="M884.363636 139.636364a116.363636 116.363636 0 0 1 116.363637 116.363636v535.272727a116.363636 116.363636 0 0 1-116.363637 116.363637H139.636364a116.363636 116.363636 0 0 1-116.363637-116.363637V256a116.363636 116.363636 0 0 1 116.363637-116.363636zM116.363636 269.661091V791.272727a23.272727 23.272727 0 0 0 23.272728 23.272728h744.727272a23.272727 23.272727 0 0 0 23.272728-23.272728V270.801455L581.282909 551.330909a93.090909 93.090909 0 0 1-115.618909 4.561455l-4.096-3.165091L116.363636 269.661091zM809.122909 232.727273H218.158545l302.429091 248.017454L809.122909 232.727273z"
      fill="#ffffff"
    />
  </svg>
);

// 新的样式组件 - 三栏布局
const StyledDetailContainer = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr 280px;
  gap: 30px;
  max-width: 1400px;
  margin: 0 auto;
  padding: 120px 20px 40px; /* 增加顶部padding避免被菜单覆盖 */
  min-height: 100vh;
  position: relative;
  z-index: 1; /* 确保内容在菜单下方 */
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    grid-template-columns: 250px 1fr 250px;
    gap: 20px;
    padding: 100px 20px 40px;
  }

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 60px 10px 40px; /* 减少顶部内边距，因为返回按钮现在在文章标题上方 */
    width: 100%;
    max-width: 100vw;
    overflow-x: hidden;
    margin: 0;
  }
`;

// 文章头部返回按钮 - 用于移动端
const StyledArticleBackButton = styled.button`
  background: transparent;
  color: var(--green);
  border: 1px solid var(--green);
  padding: 8px 16px;
  border-radius: 20px;
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-bottom: 20px;
  display: none; /* 桌面端隐藏 */
  align-items: center;
  gap: 6px;
  width: fit-content;

  &:hover {
    background: var(--green);
    color: var(--navy);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(100, 255, 218, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.25s ease;
  }

  &:hover svg {
    transform: translateX(-2px);
  }

  @media (max-width: 968px) {
    display: flex; /* 移动端显示 */
    margin-top: 20px; /* 与顶部保持距离，避免与logo重叠 */
  }
`;

// 返回按钮样式 - 重新设计版本
const StyledBackButton = styled.button`
  background: transparent;
  color: var(--green);
  border: 1px solid var(--green);
  padding: 8px 16px;
  border-radius: 20px;
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.25s ease;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  position: relative;
  z-index: 10;

  &:hover {
    background: var(--green);
    color: var(--navy);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(100, 255, 218, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
    transition: transform 0.25s ease;
  }

  &:hover svg {
    transform: translateX(-2px);
  }

  @media (max-width: 968px) {
    /* 在移动端隐藏左侧列表中的返回按钮 */
    display: none;
  }
`;

// 左侧文章列表
const StyledArticleList = styled.div`
  background: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 20px;
  height: fit-content;
  max-height: none; /* 移除高度限制，让内容自然滚动 */
  overflow-y: visible; /* 移除滚动限制 */
  position: static; /* 改为静态定位，随内容滚动 */

  /* 优化滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--navy);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--green);
    border-radius: 3px;

    &:hover {
      background: var(--green-tint);
    }
  }

  @media (max-width: 1200px) {
    max-height: none;
  }

  @media (max-width: 968px) {
    order: 2;
    width: 100%;
    position: static;
    max-height: none;
    margin-top: 20px;
    overflow-y: visible;
    /* 移动端优化触摸滚动 */
    -webkit-overflow-scrolling: touch;
    overflow-x: hidden;
  }

  .list-header {
    margin-bottom: 20px;

    h3 {
      color: var(--lightest-slate);
      font-size: var(--fz-lg);
      margin: 20px 0 0 0;
      padding-bottom: 10px;
      border-bottom: 1px solid var(--navy);
    }
  }

  .article-items {
    max-height: calc(100vh - 240px);
    overflow-y: auto;

    /* 优化滚动条样式 */
    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(100, 255, 218, 0.3);
      border-radius: 2px;

      &:hover {
        background: rgba(100, 255, 218, 0.5);
      }
    }

    @media (max-width: 968px) {
      max-height: none; /* 移除高度限制 */
      overflow-y: visible; /* 移除滚动限制 */
      /* 移动端优化触摸滚动 */
      -webkit-overflow-scrolling: touch;
    }
  }

  .article-item {
    padding: 15px;
    margin-bottom: 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    border-left: 3px solid transparent;
    border: 1px solid transparent;
    position: relative;

    &:hover {
      background: var(--navy);
      border-left-color: var(--green);
      border-color: var(--green);
    }

    &.active {
      background: var(--navy);
      border-left-color: var(--green);
      border-color: var(--green);
    }

    &.loading {
      opacity: 0.6;
      pointer-events: none;

      &::after {
        content: '';
        position: absolute;
        top: 50%;
        right: 15px;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
        border: 2px solid var(--green);
        border-top: 2px solid transparent;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      }
    }

    @keyframes spin {
      0% {
        transform: translateY(-50%) rotate(0deg);
      }
      100% {
        transform: translateY(-50%) rotate(360deg);
      }
    }

    .article-title {
      color: var(--lightest-slate);
      font-size: var(--fz-md);
      font-weight: 600;
      margin-bottom: 8px;
      line-height: 1.4;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .article-date {
      color: var(--green);
      font-size: var(--fz-xs);
      font-family: var(--font-mono);
      margin-bottom: 5px;
    }

    .article-excerpt {
      display: none; /* 隐藏详情，只显示标题 */
    }
  }
`;

// 中间文章内容
const StyledArticleContent = styled.div`
  background: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 40px;
  min-height: calc(100vh - 100px);
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media (max-width: 968px) {
    order: 1;
    padding: 30px 20px;
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
    margin: 0;
  }

  @media (max-width: 360px) {
    padding: 15px 12px;
  }
`;

// 文章头部信息
const StyledArticleHeader = styled.div`
  margin-bottom: 40px;
  padding-bottom: 30px;
  border-bottom: 1px solid var(--navy);

  .article-title {
    color: var(--lightest-slate);
    font-size: var(--fz-heading);
    font-weight: 700;
    line-height: 1.2;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: var(--fz-xxl);
    }
  }

  .article-meta {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
    flex-wrap: wrap;

    .publish-date {
      color: var(--slate);
      font-size: var(--fz-sm);
      font-family: var(--font-mono);
    }

    .read-time {
      color: var(--slate);
      font-size: var(--fz-sm);
      font-family: var(--font-mono);
    }
  }

  .article-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 25px;

    .tag {
      background: var(--navy);
      color: var(--green);
      padding: 5px 12px;
      border-radius: 15px;
      font-size: var(--fz-xs);
      font-family: var(--font-mono);
      border: 1px solid var(--green);
    }
  }
`;

// 社交媒体分享按钮样式
const StyledSocialShare = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 20px;
  padding: 20px 0;
  border-top: 1px solid var(--navy);

  .share-label {
    color: var(--lightest-slate);
    font-size: var(--fz-sm);
    font-weight: 600;
    margin-right: 10px;
  }

  .share-buttons {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
  }

  .share-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--navy);
    border: 1px solid var(--light-navy);
    color: var(--lightest-slate);
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;

    &:hover {
      background: var(--green);
      color: var(--navy);
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(100, 255, 218, 0.3);
    }

    &.twitter:hover {
      background: #1da1f2;
      color: black;
    }

    &.telegram:hover {
      background: #0088cc;
      color: white;
    }

    &.wechat:hover {
      background: #07c160;
      color: white;
    }

    &.facebook:hover {
      background: #1877f2;
      color: white;
    }

    &.linkedin:hover {
      background: #0077b5;
      color: white;
    }

    &.whatsapp:hover {
      background: #25d366;
      color: white;
    }

    &.copy:hover {
      background: var(--green);
      color: var(--navy);
    }

    &.native:hover {
      background: var(--green);
      color: var(--navy);
    }

    svg {
      width: 18px;
      height: 18px;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;

    .share-label {
      margin-right: 0;
    }

    .share-buttons {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;

// 文章正文内容样式
const StyledArticleBody = styled.div`
  .cover-image {
    margin: 30px 0;
    text-align: center;

    img {
      max-width: 100%;
      height: auto;
      border-radius: var(--border-radius);
      box-shadow: 0 10px 30px -15px var(--navy-shadow);
    }
  }

  .article-content {
    line-height: 1.8;
    font-size: var(--fz-lg);
    color: var(--light-slate);
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
    box-sizing: border-box;

    /* 确保换行符正确显示 */
    white-space: pre-wrap;

    /* 移动端优化 */
    @media (max-width: 768px) {
      font-size: var(--fz-md);
      line-height: 1.7;
      padding: 0;
      margin: 0;
      width: 100%;
      overflow-x: hidden;
    }

    @media (max-width: 480px) {
      font-size: var(--fz-sm);
      line-height: 1.6;
    }

    /* 标题样式 */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: var(--lightest-slate);
      font-weight: 600;
      margin: 40px 0 20px 0;
      line-height: 1.3;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;

      &:first-child {
        margin-top: 0;
      }

      @media (max-width: 768px) {
        margin: 30px 0 15px 0;
      }

      @media (max-width: 480px) {
        margin: 25px 0 12px 0;
      }
    }

    h1 {
      font-size: 2.5rem;
      border-bottom: 2px solid var(--green);
      padding-bottom: 10px;

      @media (max-width: 768px) {
        font-size: 2rem;
      }

      @media (max-width: 480px) {
        font-size: 1.75rem;
      }
    }

    h2 {
      font-size: 2rem;
      border-bottom: 1px solid var(--light-navy);
      padding-bottom: 8px;

      @media (max-width: 768px) {
        font-size: 1.5rem;
      }

      @media (max-width: 480px) {
        font-size: 1.375rem;
      }
    }

    h3 {
      font-size: 1.5rem;
      color: var(--green);

      @media (max-width: 768px) {
        font-size: 1.25rem;
      }

      @media (max-width: 480px) {
        font-size: 1.125rem;
      }
    }

    h4 {
      font-size: 1.25rem;

      @media (max-width: 768px) {
        font-size: 1.125rem;
      }

      @media (max-width: 480px) {
        font-size: 1rem;
      }
    }

    h5,
    h6 {
      font-size: 1.1rem;

      @media (max-width: 768px) {
        font-size: 1rem;
      }

      @media (max-width: 480px) {
        font-size: 0.95rem;
      }
    }

    /* 段落样式 */
    p {
      margin: 20px 0;
      text-align: justify;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow-wrap: break-word;
      max-width: 100%;

      @media (max-width: 768px) {
        margin: 16px 0;
        text-align: left;
      }

      @media (max-width: 480px) {
        margin: 14px 0;
      }
    }

    /* 链接样式 */
    a {
      color: var(--green);
      text-decoration: none;
      border-bottom: 1px solid transparent;
      transition: border-color 0.3s ease;

      &:hover {
        border-bottom-color: var(--green);
        color: var(--green-tint);
      }
    }

    /* 列表样式 */
    ul,
    ol {
      margin: 20px 0;
      padding-left: 30px;

      li {
        margin: 10px 0;
        white-space: pre-wrap;

        &::marker {
          color: var(--green);
        }
      }
    }

    /* 代码块样式 - 增强版本，支持多种情况 */
    pre {
      background: var(--light-navy);
      border: 1px solid var(--lightest-navy);
      border-radius: var(--border-radius);
      padding: 20px 50px 20px 20px; /* 右侧留出复制按钮空间 */
      margin: 25px 0;
      overflow-x: auto;
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      line-height: 1.5;
      white-space: pre;
      word-wrap: normal;
      position: relative;
      max-width: 100%;
      box-sizing: border-box;

      @media (max-width: 768px) {
        padding: 15px 40px 15px 15px;
        margin: 20px 0;
        font-size: var(--fz-xs);
        border-radius: 6px;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }

      @media (max-width: 480px) {
        padding: 12px 36px 12px 12px;
        margin: 16px 0;
        font-size: 12px;
      }

      /* 复制按钮样式 */
      .copy-button {
        position: absolute;
        top: 8px;
        right: 8px;
        background: var(--navy);
        border: 1px solid var(--lightest-navy);
        color: var(--green);
        padding: 4px 8px;
        border-radius: 4px;
        font-size: var(--fz-xxs);
        font-family: var(--font-mono);
        cursor: pointer;
        transition: all 0.2s ease;
        opacity: 0.7;
        z-index: 10;
        display: flex;
        align-items: center;

        &:hover {
          opacity: 1;
          background: var(--green);
          color: var(--navy);
          transform: translateY(-1px);
        }

        &.copied {
          background: var(--green);
          color: var(--navy);
          opacity: 1;
        }

        svg {
          width: 12px;
          height: 12px;
          margin-right: 4px;
        }
      }

      code {
        background: none;
        padding: 0;
        border: none;
        color: var(--lightest-slate);
        white-space: pre;
        word-wrap: normal;
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        display: block;
        width: 100%;

        /* 确保代码内容正确换行 */
        overflow-wrap: normal;
        word-break: normal;

        /* 保持原始格式 */
        text-align: left;
        direction: ltr;
      }
    }

    /* 行内代码样式 */
    code {
      background: var(--light-navy);
      color: var(--green);
      padding: 3px 6px;
      border-radius: 3px;
      font-family: var(--font-mono);
      font-size: 0.9em;
      border: 1px solid var(--lightest-navy);
      white-space: pre-wrap;
    }

    /* 处理没有pre包裹的代码块 */
    code:not(pre code) {
      display: inline;
      white-space: pre-wrap;
    }

    /* 处理多行代码块（即使没有pre标签） */
    code[class*='language-'],
    code[class*='lang-'] {
      display: block;
      background: var(--light-navy);
      border: 1px solid var(--lightest-navy);
      border-radius: var(--border-radius);
      padding: 20px 50px 20px 20px; /* 右侧留出复制按钮空间 */
      margin: 25px 0;
      overflow-x: auto;
      font-family: var(--font-mono);
      font-size: var(--fz-sm);
      line-height: 1.5;
      white-space: pre;
      word-wrap: normal;
      color: var(--lightest-slate);
      position: relative;

      /* 确保代码块内容正确显示 */
      text-align: left;
      direction: ltr;
      overflow-wrap: normal;
      word-break: normal;

      /* 添加语言标识 */
      &::after {
        content: attr(class);
        position: absolute;
        top: 8px;
        right: 50px; /* 为复制按钮让出空间 */
        background: var(--navy);
        color: var(--green);
        padding: 2px 8px;
        border-radius: 3px;
        font-size: var(--fz-xxs);
        font-family: var(--font-mono);
        text-transform: uppercase;
        opacity: 0.7;
      }

      &.language-python::after {
        content: 'Python';
      }
      &.language-javascript::after,
      &.language-js::after {
        content: 'JavaScript';
      }
      &.language-typescript::after,
      &.language-ts::after {
        content: 'TypeScript';
      }
      &.language-jsx::after {
        content: 'JSX';
      }
      &.language-tsx::after {
        content: 'TSX';
      }
      &.language-css::after {
        content: 'CSS';
      }
      &.language-html::after {
        content: 'HTML';
      }
      &.language-json::after {
        content: 'JSON';
      }
      &.language-bash::after,
      &.language-shell::after {
        content: 'Shell';
      }
      &.language-sql::after {
        content: 'SQL';
      }
      &.language-yaml::after,
      &.language-yml::after {
        content: 'YAML';
      }
      &.language-xml::after {
        content: 'XML';
      }
      &.language-markdown::after,
      &.language-md::after {
        content: 'Markdown';
      }
      &.language-text::after {
        content: 'Text';
      }

      /* 复制按钮样式 */
      .copy-button {
        position: absolute;
        top: 8px;
        right: 8px;
        background: var(--navy);
        border: 1px solid var(--lightest-navy);
        color: var(--green);
        padding: 4px 8px;
        border-radius: 4px;
        font-size: var(--fz-xxs);
        font-family: var(--font-mono);
        cursor: pointer;
        transition: all 0.2s ease;
        opacity: 0.7;
        z-index: 10;

        &:hover {
          opacity: 1;
          background: var(--green);
          color: var(--navy);
          transform: translateY(-1px);
        }

        &.copied {
          background: var(--green);
          color: var(--navy);
          opacity: 1;
        }

        svg {
          width: 12px;
          height: 12px;
          margin-right: 4px;
        }
      }
    }

    /* 引用样式 */
    blockquote {
      border-left: 4px solid var(--green);
      background: var(--light-navy);
      margin: 25px 0;
      padding: 20px;
      border-radius: 0 var(--border-radius) var(--border-radius) 0;

      p {
        margin: 0;
        font-style: italic;
        color: var(--slate);
        white-space: pre-wrap;
      }
    }

    /* 表格样式 */
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 25px 0;
      background: var(--light-navy);
      border-radius: var(--border-radius);
      overflow: hidden;
      max-width: 100%;
      box-sizing: border-box;

      @media (max-width: 768px) {
        margin: 20px 0;
        font-size: var(--fz-sm);
        overflow-x: auto;
        display: block;
        white-space: nowrap;
      }

      @media (max-width: 480px) {
        margin: 16px 0;
        font-size: var(--fz-xs);
      }

      th,
      td {
        padding: 12px 15px;
        text-align: left;
        border-bottom: 1px solid var(--lightest-navy);
        white-space: pre-wrap;

        @media (max-width: 768px) {
          padding: 8px 10px;
          white-space: nowrap;
        }

        @media (max-width: 480px) {
          padding: 6px 8px;
        }
      }

      th {
        background: var(--navy);
        color: var(--lightest-slate);
        font-weight: 600;
      }

      tr:hover {
        background: var(--lightest-navy);
      }
    }

    /* 图片样式 */
    img {
      max-width: 100%;
      height: auto;
      border-radius: var(--border-radius);
      margin: 20px 0;
      box-shadow: 0 5px 15px -5px var(--navy-shadow);
      display: block;

      @media (max-width: 768px) {
        margin: 16px 0;
        border-radius: 6px;
      }

      @media (max-width: 480px) {
        margin: 14px 0;
        border-radius: 4px;
      }
    }

    /* 分割线样式 */
    hr {
      border: none;
      height: 1px;
      background: linear-gradient(90deg, transparent, var(--green), transparent);
      margin: 40px 0;
    }

    /* 强调文本样式 */
    strong {
      color: var(--lightest-slate);
      font-weight: 600;
    }

    em {
      color: var(--green);
      font-style: italic;
    }

    /* 删除线样式 */
    del {
      color: var(--slate);
      text-decoration: line-through;
    }

    /* 标记文本样式 */
    mark {
      background: var(--green-tint);
      color: var(--navy);
      padding: 2px 4px;
      border-radius: 3px;
    }

    /* 处理原始文本中的换行符 */
    br {
      display: block;
      margin: 0.5em 0;
      content: '';
    }

    /* 处理可能的markdown格式 */
    .markdown-body {
      white-space: pre-wrap;
    }

    /* 移动端优化 */
    @media (max-width: 768px) {
      font-size: var(--fz-md);

      pre,
      code[class*='language-'],
      code[class*='lang-'] {
        font-size: var(--fz-xs);
        padding: 15px;
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }

      h1 {
        font-size: 2rem;
      }

      h2 {
        font-size: 1.5rem;
      }

      h3 {
        font-size: 1.25rem;
      }
    }
  }
`;

// 打赏码样式
const StyledDonationSection = styled.div`
  margin-top: 50px;
  padding: 30px;
  background: var(--navy);
  border-radius: var(--border-radius);
  text-align: center;
  border: 1px solid var(--light-navy);

  h3 {
    color: var(--lightest-slate);
    margin-bottom: 20px;
    font-size: var(--fz-lg);
  }

  .donation-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;

    img {
      max-width: 200px;
      height: auto;
      border-radius: 8px;
      box-shadow: 0 5px 15px -5px var(--navy-shadow);
    }

    p {
      color: var(--slate);
      font-size: var(--fz-sm);
      margin: 0;
    }
  }
`;

// 右侧边栏
const StyledSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 968px) {
    order: 3;
    flex-direction: column; /* 移动端改为垂直布局 */
    gap: 20px;
    margin-top: 30px;
    width: 100%;
  }

  @media (max-width: 640px) {
    gap: 15px;
    margin-top: 20px;
  }
`;

// 目录组件
const StyledTableOfContents = styled.div`
  background: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 20px;
  position: static; /* 改为静态定位，随内容滚动 */
  max-height: 600px; /* 设置最大高度 */
  overflow-y: auto; /* 启用垂直滚动 */

  /* 自定义滚动条样式 */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(100, 255, 218, 0.3);
    border-radius: 3px;
    transition: background 0.3s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(100, 255, 218, 0.6);
  }

  /* Firefox 滚动条样式 */
  scrollbar-width: thin;
  scrollbar-color: rgba(100, 255, 218, 0.3) transparent;

  /* 滚动条只在悬停时显示 */
  &:not(:hover)::-webkit-scrollbar-thumb {
    background: transparent;
  }

  @media (max-width: 968px) {
    display: none; /* 在移动端隐藏桌面端目录 */
  }

  h4 {
    color: var(--lightest-slate);
    font-size: var(--fz-md);
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--navy);
    font-weight: 600;
  }

  .toc-list {
    list-style: none;
    padding: 0;
    margin: 0;

    .toc-item {
      margin-bottom: 4px;
      cursor: pointer;
      padding: 4px 0;
      font-size: var(--fz-xs); /* 更小的字体 */
      color: var(--lightest-slate); /* 改为白色 */
      line-height: 1.3;
      border-left: 2px solid transparent;
      padding-left: 8px;
      transition: all 0.2s ease;
      border-radius: 3px;

      &:hover {
        color: var(--green); /* 悬停时变为绿色 */
        border-left-color: var(--green);
        background: rgba(100, 255, 218, 0.05);
      }

      &.active {
        color: var(--green);
        border-left-color: var(--green);
        background: rgba(100, 255, 218, 0.1);
        font-weight: 500;
      }

      &.level-1 {
        font-weight: 500;
        padding-left: 8px;
        font-size: var(--fz-xs);
        color: var(--lightest-slate); /* 一级标题白色 */
      }

      &.level-2 {
        padding-left: 16px;
        font-size: calc(var(--fz-xs) - 1px);
        color: var(--light-slate); /* 二级标题稍微暗一点的白色 */
      }

      &.level-3 {
        padding-left: 24px;
        font-size: calc(var(--fz-xs) - 2px);
        color: var(--light-slate); /* 三级标题稍微暗一点的白色 */
      }

      &.level-4 {
        padding-left: 32px;
        font-size: calc(var(--fz-xs) - 2px);
        color: var(--slate); /* 四级标题稍微暗一点 */
      }

      &.level-5,
      &.level-6 {
        padding-left: 40px;
        font-size: calc(var(--fz-xs) - 3px);
        color: var(--slate); /* 五六级标题稍微暗一点 */
      }
    }
  }
`;

// 移动端目录样式
const StyledMobileTableOfContents = styled.div`
  display: none; /* 默认隐藏 */

  @media (max-width: 968px) {
    display: block; /* 在移动端显示 */
    background: var(--light-navy);
    border-radius: var(--border-radius);
    padding: 15px;
    margin-bottom: 20px;
    border: 1px solid var(--lightest-navy);
  }

  h4 {
    color: var(--lightest-slate);
    font-size: var(--fz-md);
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--navy);
    font-weight: 600;
  }

  .toc-list {
    list-style: none;
    padding: 0;
    margin: 0;

    .toc-item {
      margin-bottom: 8px;
      cursor: pointer;
      padding: 8px 0;
      font-size: var(--fz-sm); /* 移动端使用稍大的字体 */
      color: var(--lightest-slate);
      line-height: 1.4;
      border-left: 3px solid transparent;
      padding-left: 12px;
      transition: all 0.2s ease;
      border-radius: 4px;

      &:hover {
        color: var(--green);
        border-left-color: var(--green);
        background: rgba(100, 255, 218, 0.05);
      }

      &.active {
        color: var(--green);
        border-left-color: var(--green);
        background: rgba(100, 255, 218, 0.1);
        font-weight: 500;
      }

      &.level-1 {
        font-weight: 600;
        padding-left: 12px;
        font-size: var(--fz-sm);
        color: var(--lightest-slate);
      }

      &.level-2 {
        padding-left: 20px;
        font-size: calc(var(--fz-sm) - 1px);
        color: var(--light-slate);
      }

      &.level-3 {
        padding-left: 28px;
        font-size: calc(var(--fz-sm) - 2px);
        color: var(--light-slate);
      }

      &.level-4 {
        padding-left: 36px;
        font-size: calc(var(--fz-sm) - 2px);
        color: var(--slate);
      }

      &.level-5,
      &.level-6 {
        padding-left: 44px;
        font-size: calc(var(--fz-sm) - 3px);
        color: var(--slate);
      }
    }
  }
`;

// 文章导航区域 - 简洁版本
const StyledArticleNavigation = styled.div`
  margin-top: 40px;
  padding: 20px 0;
  border-top: 1px solid var(--lightest-navy);
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
    padding: 15px 0;
    margin-top: 30px;
  }
`;

const StyledNavButton = styled.button`
  background: transparent;
  border: 1px solid var(--lightest-navy);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--lightest-slate);
  text-decoration: none;
  transition: all 0.25s ease;
  cursor: pointer;
  flex: 1;
  max-width: 280px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 60px;

  &:hover {
    background: var(--lightest-navy);
    border-color: var(--green);
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    &:hover {
      transform: none;
      border-color: var(--lightest-navy);
      background: transparent;
      box-shadow: none;
    }
  }

  .nav-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .nav-label {
    font-size: var(--fz-xs);
    color: var(--slate);
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .nav-title {
    font-size: var(--fz-sm);
    color: var(--lightest-slate);
    font-weight: 400;
    line-height: 1.3;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .nav-arrow {
    width: 16px;
    height: 16px;
    color: var(--green);
    transition: transform 0.25s ease;
  }

  &.next {
    text-align: right;
    flex-direction: row-reverse;

    .nav-content {
      text-align: right;
    }
  }

  &.prev:hover .nav-arrow {
    transform: translateX(-2px);
  }

  &.next:hover .nav-arrow {
    transform: translateX(2px);
  }

  @media (max-width: 768px) {
    max-width: 100%;
    text-align: left;
    flex-direction: row;

    &.next {
      text-align: left;
      flex-direction: row;

      .nav-content {
        text-align: left;
      }
    }
  }
`;

// 广告区域
const StyledAdSection = styled.div`
  background: var(--light-navy);
  border-radius: var(--border-radius);
  padding: 20px;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: static; /* 改为静态定位，随内容滚动 */
  margin-top: ${props =>
    props.hasTableOfContents ? '30px' : '0'}; /* 有目录时添加间距，没有目录时与顶部对齐 */

  @media (max-width: 1200px) {
    min-height: 250px;
  }

  @media (max-width: 968px) {
    position: static;
    width: 100%;
    min-height: 200px;
    margin-top: 20px;
    padding: 15px;
  }

  @media (max-width: 640px) {
    min-height: 150px;
    padding: 10px;
  }

  h4 {
    color: var(--lightest-slate);
    font-size: var(--fz-md);
    margin-bottom: 15px;
    font-weight: 600;

    @media (max-width: 640px) {
      font-size: var(--fz-sm);
      margin-bottom: 10px;
    }
  }

  .ad-content {
    width: 100%;
    text-align: center;

    /* 广告内容样式 */
    ins {
      display: block !important;
      width: 100%;
      min-height: 250px;

      @media (max-width: 968px) {
        min-height: 200px;
      }

      @media (max-width: 640px) {
        min-height: 150px;
      }
    }

    /* 确保广告脚本能正确执行 */
    script {
      display: block;
    }

    /* 移动端广告适配 */
    @media (max-width: 640px) {
      ins[data-ad-format='auto'] {
        min-height: 100px;
      }
    }
  }

  .ad-placeholder {
    color: var(--slate);
    text-align: center;
    font-size: var(--fz-sm);
    font-style: italic;
    opacity: 0.7;

    @media (max-width: 640px) {
      font-size: var(--fz-xs);
    }
  }
`;

const StyledBlogContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 10px;
  }
`;

const StyledSocialContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start; /* 改为左对齐 */
  gap: 15px;
  margin: 30px 0;
  flex-wrap: wrap;

  .social-label {
    font-size: var(--fz-sm);
    color: var(--light-slate);
    margin-right: 10px;
    display: none; /* 隐藏socialShare文案 */
  }
`;

const StyledSocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: var(--light-slate);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);

  &:hover {
    background: ${props => props.hoverColor || 'var(--green)'};
    color: ${props => {
    // 如果hover颜色是白色或浅色，使用深色文字
    const hoverColor = props.hoverColor;
    if (
      hoverColor === '#ffffff' ||
        hoverColor === 'white' ||
        hoverColor === '#f5f5f5' ||
        hoverColor === '#FFFFFF'
    ) {
      return 'black';
    }
    // 对于其他颜色，使用白色文字
    return 'white';
  }};
    transform: translateY(-2px);
    box-shadow: 0 10px 30px -15px ${props => props.hoverColor || 'var(--green)'};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const StyledFeaturedPost = styled.div`
  background: linear-gradient(135deg, var(--navy) 0%, var(--light-navy) 100%);
  border-radius: var(--border-radius);
  padding: 40px;
  margin-bottom: 80px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
  display: flex;
  align-items: center;
  gap: 30px;

  &:hover {
    transform: translateY(-5px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(100, 255, 218, 0.1) 50%,
      transparent 70%
    );
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover::before {
    opacity: 1;
  }

  .featured-content {
    flex: 1;
  }

  .featured-image {
    flex-shrink: 0;
    width: 200px;
    height: 150px;
    border-radius: 12px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .featured-badge {
    display: inline-block;
    background-color: var(--green);
    color: var(--navy);
    padding: 5px 15px;
    border-radius: 20px;
    font-size: var(--fz-xs);
    font-weight: 600;
    margin-bottom: 20px;
    font-family: var(--font-mono);
  }

  .featured-title {
    font-size: var(--fz-heading);
    font-weight: 600;
    color: var(--lightest-slate);
    margin-bottom: 15px;
    line-height: 1.3;

    @media (max-width: 768px) {
      font-size: var(--fz-xxl);
    }
  }

  .featured-description {
    color: var(--slate);
    font-size: var(--fz-lg);
    line-height: 1.6;
    margin-bottom: 20px;
    max-width: 600px;
  }

  .featured-meta {
    display: flex;
    align-items: center;
    gap: 20px;
    font-size: var(--fz-sm);
    color: var(--light-slate);
    font-family: var(--font-mono);

    .featured-date {
      color: var(--green);
    }

    .featured-tags {
      display: flex;
      gap: 10px;

      span {
        background-color: var(--light-navy);
        padding: 3px 8px;
        border-radius: var(--border-radius);
        font-size: var(--fz-xxs);
      }
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;

    .featured-image {
      width: 100%;
      max-width: 300px;
    }
  }
`;

const StyledBlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  max-width: 1200px;
  width: 100%;

  @media (max-width: 1080px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 25px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  /* 确保所有网格项目宽度一致 */
  > * {
    width: 100%;
    min-width: 0; /* 允许内容收缩 */
  }
`;

const StyledPagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin-top: 60px;

  button {
    background: none;
    border: 1px solid var(--green);
    color: var(--green);
    padding: 10px 20px;
    border-radius: var(--border-radius);
    cursor: pointer;
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      background-color: var(--green);
      color: var(--navy);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    &.active {
      background-color: var(--green);
      color: var(--navy);
    }
  }

  .page-info {
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    margin: 0 10px;
  }
`;

const StyledLoadingContainer = styled.div`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  min-height: 50vh;

  .loading-text {
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-md);
  }
`;

const StyledErrorContainer = styled.div`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  min-height: 50vh;

  .error-text {
    color: var(--red);
    font-family: var(--font-mono);
    font-size: var(--fz-md);
  }
`;

const StyledModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  .modal-content {
    background: white;
    padding: 30px;
    border-radius: 12px;
    text-align: center;
    max-width: 400px;
    width: 90%;
    position: relative;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);

    .close-button {
      position: absolute;
      top: 15px;
      right: 20px;
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: #666;

      &:hover {
        color: #000;
      }
    }

    h3 {
      margin: 0 0 20px 0;
      color: #333;
      font-size: 20px;
    }

    .qr-image {
      width: 200px;
      height: 200px;
      object-fit: cover;
      border-radius: 8px;
      margin-bottom: 15px;
      border: 1px solid #eee;
    }

    .share-qr-container {
      margin: 20px 0;
    }

    .share-title {
      font-weight: 600;
      color: #333;
      margin: 15px 0 10px 0;
      font-size: 16px;
      line-height: 1.4;
    }

    .share-actions {
      margin-top: 20px;

      .copy-url-btn {
        background: var(--green);
        color: white;
        border: none;
        padding: 10px 20px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
        transition: all 0.3s ease;

        &:hover {
          background: var(--green-tint);
          transform: translateY(-1px);
        }
      }
    }

    p {
      margin: 10px 0;
      color: #666;
      font-size: 14px;
      line-height: 1.5;
    }
  }
`;

/**
 * MyBlog 页面组件
 * 完全从 API 获取数据展示文章列表
 */
const MyBlogPage = ({ location }) => {
  const { t, currentLanguage } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]); // 存储所有文章用于左侧列表
  const [featuredPost, setFeaturedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPostId, setLoadingPostId] = useState(null);
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [error, setError] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showWechatModal, setShowWechatModal] = useState(false);
  const [currentShareUrl, setCurrentShareUrl] = useState('');
  const [currentShareTitle, setCurrentShareTitle] = useState('');
  const [tableOfContents, setTableOfContents] = useState([]);
  const [activeHeading, setActiveHeading] = useState('');

  const revealTitle = useRef(null);
  const revealSocial = useRef(null);
  const revealFeatured = useRef(null);
  const revealGrid = useRef(null);
  const revealPagination = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const POSTS_PER_PAGE = 10;

  // 社交媒体配置
  const socialMediaConfig = [
    {
      name: 'X',
      icon: XIcon,
      url: 'https://x.com/0xlauyu',
      color: '#ffffff',
    },
    {
      name: 'Xiaohongshu',
      icon: XiaohongshuIcon,
      url: 'https://www.xiaohongshu.com/user/profile/643f8136000000001400d1c2',
      color: '#FF0035',
    },
    {
      name: 'Telegram',
      icon: TelegramIcon,
      url: 'https://t.me/id8fun',
      color: '#0088cc',
    },
    {
      name: 'Weixin',
      icon: WeixinIcon,
      url: '#',
      color: '#07c160',
      onClick: () => setShowWechatModal(true),
    },
    {
      name: 'Email',
      icon: MailIcon,
      url: 'mailto:ylau@id8.fun',
      color: '#ffffff',
    },
  ];

  // 生成目录结构
  const generateTableOfContents = content => {
    if (!content) {
      return [];
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;

    const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
    const toc = [];

    headings.forEach((heading, index) => {
      const level = parseInt(heading.tagName.charAt(1));
      const text = heading.textContent.trim();
      // 生成更稳定的ID，基于文本内容
      const id = `heading-${text
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fff]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')}-${index}`;

      // 为标题添加ID
      heading.id = id;

      toc.push({
        id,
        text,
        level,
      });
    });

    // 更新原始内容中的标题ID
    return { toc, updatedContent: tempDiv.innerHTML };
  };

  // 分享功能函数
  const shareToTwitter = (post, url) => {
    const title = post.title;
    const description =
      post.description || post.content?.replace(/<[^>]*>/g, '').substring(0, 100) || '';
    const hashtags = post.tags ? parseTags(post.tags).join(',') : 'blog,tech';

    // 构建Twitter分享URL，包含标题、描述和标签
    const text = encodeURIComponent(`${title}\n\n${description}`);
    const shareUrl = encodeURIComponent(url);
    const hashtagsParam = encodeURIComponent(hashtags);
    const twitterUrl = `https://twitter.com/intent/tweet?text=${text}&url=${shareUrl}&hashtags=${hashtagsParam}`;

    window.open(twitterUrl, '_blank', 'width=550,height=420');
  };

  const shareToTelegram = (post, url) => {
    const title = post.title;
    const description =
      post.description || post.content?.replace(/<[^>]*>/g, '').substring(0, 150) || '';
    const author = 'Y Lau - ID8 Blog';

    // 构建完整的分享文本
    const shareText = `📖 ${title}\n\n${description}\n\n👤 ${author}`;
    const text = encodeURIComponent(shareText);
    const shareUrl = encodeURIComponent(url);
    const telegramUrl = `https://t.me/share/url?url=${shareUrl}&text=${text}`;

    // 检测是否在移动设备上
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

    if (isMobile) {
      // 移动设备尝试使用Telegram应用协议
      const telegramAppUrl = `tg://msg_url?url=${shareUrl}&text=${text}`;
      window.location.href = telegramAppUrl;

      // 如果应用协议失败，延迟后打开网页版
      setTimeout(() => {
        window.open(telegramUrl, '_blank');
      }, 1000);
    } else {
      window.open(telegramUrl, '_blank', 'width=550,height=420');
    }
  };

  const shareToWechat = (post, url) => {
    // 检测是否在微信内置浏览器
    const isWechat = /MicroMessenger/i.test(navigator.userAgent);

    if (isWechat) {
      // 在微信内，显示分享提示
      alert(t('wechatShareTip') || '请点击右上角菜单分享到朋友圈或发送给朋友');
    } else {
      // 非微信环境，生成当前文章的二维码
      setCurrentShareUrl(url);
      setCurrentShareTitle(post.title);
      setShowWechatModal(true);
    }
  };

  const shareToFacebook = (post, url) => {
    const title = encodeURIComponent(post.title);
    const description = encodeURIComponent(
      post.description || post.content?.replace(/<[^>]*>/g, '').substring(0, 150) || '',
    );
    const shareUrl = encodeURIComponent(url);

    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}&quote=${title} - ${description}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
  };

  const shareToLinkedIn = (post, url) => {
    const title = encodeURIComponent(post.title);
    const summary = encodeURIComponent(
      post.description || post.content?.replace(/<[^>]*>/g, '').substring(0, 150) || '',
    );
    const source = encodeURIComponent('ID8 Blog');
    const shareUrl = encodeURIComponent(url);

    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}&title=${title}&summary=${summary}&source=${source}`;
    window.open(linkedInUrl, '_blank', 'width=550,height=420');
  };

  const shareToWhatsApp = (post, url) => {
    const title = post.title;
    const description =
      post.description || post.content?.replace(/<[^>]*>/g, '').substring(0, 100) || '';
    const shareText = `📖 *${title}*\n\n${description}\n\n🔗 ${url}\n\n📝 来自 ID8 Blog`;
    const text = encodeURIComponent(shareText);

    // 检测是否在移动设备上
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

    if (isMobile) {
      // 移动设备使用WhatsApp应用协议
      window.location.href = `whatsapp://send?text=${text}`;
    } else {
      // 桌面设备使用WhatsApp Web
      window.open(`https://web.whatsapp.com/send?text=${text}`, '_blank');
    }
  };

  // 复制到剪贴板函数
  const copyToClipboard = async url => {
    try {
      await navigator.clipboard.writeText(url);
      // 可以添加一个提示消息
      alert(t('linkCopied'));
    } catch (err) {
      // 降级方案
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert(t('linkCopied'));
    }
  };

  // 复制代码块函数 - 使用useCallback稳定函数引用
  const copyCodeBlock = useCallback(
    async (codeBlockId, encodedCodeText) => {
      try {
        // 解码Base64编码的代码文本
        const codeText = decodeURIComponent(atob(encodedCodeText));

        // 获取当前文章标题和URL
        const articleTitle = selectedPost?.title || '代码片段';
        const currentUrl = window.location.href;

        // 构建包含来源信息的文本
        const sourceInfo = `\n\n// 来源：${articleTitle}\n// 链接：${currentUrl}\n// 网站：ID8 Blog`;
        const textToCopy = codeText + sourceInfo;

        await navigator.clipboard.writeText(textToCopy);

        // 更新按钮状态
        const button = document.querySelector(`#${codeBlockId} .copy-button`);
        if (button) {
          const originalText = button.innerHTML;
          button.innerHTML = `
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
          </svg>
          已复制
        `;
          button.classList.add('copied');

          // 2秒后恢复原状
          setTimeout(() => {
            button.innerHTML = originalText;
            button.classList.remove('copied');
          }, 2000);
        }
      } catch (err) {
        // 降级方案
        try {
          const codeText = decodeURIComponent(atob(encodedCodeText));
          const textArea = document.createElement('textarea');
          const articleTitle = selectedPost?.title || '代码片段';
          const currentUrl = window.location.href;
          const sourceInfo = `\n\n// 来源：${articleTitle}\n// 链接：${currentUrl}\n// 网站：ID8 Blog`;
          textArea.value = codeText + sourceInfo;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);

          // 更新按钮状态
          const button = document.querySelector(`#${codeBlockId} .copy-button`);
          if (button) {
            const originalText = button.innerHTML;
            button.innerHTML = `
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
            已复制
          `;
            button.classList.add('copied');

            setTimeout(() => {
              button.innerHTML = originalText;
              button.classList.remove('copied');
            }, 2000);
          }
        } catch (decodeErr) {
          console.error('Failed to decode and copy code:', decodeErr);
          alert('复制失败，请手动选择代码进行复制');
        }
      }
    },
    [selectedPost?.title],
  );

  // 系统原生分享
  const shareNative = async (title, url) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${title} - ID8 Blog`,
          text: title,
          url: url,
        });
      } catch (error) {
        // 用户取消分享或分享失败
        if (error.name !== 'AbortError') {
          // 回退到复制链接
          copyToClipboard(url);
        }
      }
    } else {
      // 不支持原生分享，回退到复制链接
      copyToClipboard(url);
    }
  };

  // 处理文章内容，确保正确渲染
  const processArticleContent = content => {
    if (!content) {
      return '';
    }

    // 如果内容已经包含HTML标签，直接返回（已经处理过的内容）
    if (content.includes('<') && content.includes('>')) {
      // 检查是否是完整的HTML内容
      if (content.includes('<p>') || content.includes('<div>') || content.includes('<pre>')) {
        return content
          .replace(/\n\s*\n\s*\n/g, '\n\n') // 清理多余的空行
          .replace(/(<\/p>)\s*(<p>)/g, '$1\n\n$2') // 段落间添加换行
          .replace(/(<\/pre>)\s*(<pre>)/g, '$1\n\n$2') // 代码块间添加空行
          .replace(/(<\/h[1-6]>)\s*(<[^h])/g, '$1\n\n$2') // 标题后添加换行
          .replace(/(<\/blockquote>)\s*(<[^b])/g, '$1\n\n$2'); // 引用后添加换行
      }
    }

    // 如果是纯文本或Markdown格式，进行转换
    let processedContent = content;

    // 首先处理代码块（三个反引号），避免其内容被其他规则影响
    const codeBlocks = [];

    // 处理带语言标识的代码块
    processedContent = processedContent.replace(
      /```(\w+)?\s*\n?([\s\S]*?)```/g,
      (match, lang, code) => {
        const language = lang || 'text';
        // 保持代码的原始格式，不进行trim，避免破坏缩进
        const cleanCode = code
          .replace(/^\n+/, '') // 只移除开头的换行
          .replace(/\n+$/, ''); // 只移除结尾的换行
        const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
        const codeBlockId = `code-block-${codeBlocks.length}`;
        // 使用data属性存储代码内容，避免引号转义问题
        const encodedCode = btoa(encodeURIComponent(cleanCode)); // Base64编码
        codeBlocks.push(`
          <pre id="${codeBlockId}">
            <button class="copy-button" data-code="${encodedCode}" onclick="copyCodeBlock('${codeBlockId}', this.dataset.code)">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
              </svg>
              复制
            </button>
            <code class="language-${language}">${cleanCode}</code>
          </pre>
        `);
        return placeholder;
      },
    );

    // 处理没有语言标识的代码块
    processedContent = processedContent.replace(/```\s*\n?([\s\S]*?)```/g, (match, code) => {
      // 保持代码的原始格式
      const cleanCode = code
        .replace(/^\n+/, '') // 只移除开头的换行
        .replace(/\n+$/, ''); // 只移除结尾的换行
      const placeholder = `__CODE_BLOCK_${codeBlocks.length}__`;
      const codeBlockId = `code-block-${codeBlocks.length}`;
      // 使用data属性存储代码内容，避免引号转义问题
      const encodedCode = btoa(encodeURIComponent(cleanCode)); // Base64编码
      codeBlocks.push(`
        <pre id="${codeBlockId}">
          <button class="copy-button" data-code="${encodedCode}" onclick="copyCodeBlock('${codeBlockId}', this.dataset.code)">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
            </svg>
            复制
          </button>
          <code>${cleanCode}</code>
        </pre>
      `);
      return placeholder;
    });

    // 处理行内代码
    const inlineCodes = [];
    processedContent = processedContent.replace(/`([^`\n]+)`/g, (match, code) => {
      const placeholder = `__INLINE_CODE_${inlineCodes.length}__`;
      inlineCodes.push(`<code>${code}</code>`);
      return placeholder;
    });

    // 处理标题（按从大到小的顺序处理，避免冲突）- 添加ID生成
    let headingIndex = 0;
    processedContent = processedContent.replace(/^#### (.*$)/gm, (match, title) => {
      const id = `heading-${title
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fff]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')}-${headingIndex++}`;
      return `<h4 id="${id}">${title}</h4>`;
    });
    processedContent = processedContent.replace(/^### (.*$)/gm, (match, title) => {
      const id = `heading-${title
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fff]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')}-${headingIndex++}`;
      return `<h3 id="${id}">${title}</h3>`;
    });
    processedContent = processedContent.replace(/^## (.*$)/gm, (match, title) => {
      const id = `heading-${title
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fff]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')}-${headingIndex++}`;
      return `<h2 id="${id}">${title}</h2>`;
    });
    processedContent = processedContent.replace(/^# (.*$)/gm, (match, title) => {
      const id = `heading-${title
        .toLowerCase()
        .replace(/[^a-z0-9\u4e00-\u9fff]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '')}-${headingIndex++}`;
      return `<h1 id="${id}">${title}</h1>`;
    });

    // 处理粗体和斜体
    processedContent = processedContent.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    processedContent = processedContent.replace(/\*(.*?)\*/g, '<em>$1</em>');

    // 处理链接
    processedContent = processedContent.replace(
      /\[([^\]]+)\]\(([^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    );

    // 处理无序列表 - 改进正则表达式
    const listItems = [];
    processedContent = processedContent.replace(/^[\s]*[-*+] (.+)$/gm, (match, item) => {
      const placeholder = `__LIST_ITEM_${listItems.length}__`;
      listItems.push(`<li>${item}</li>`);
      return placeholder;
    });

    // 将连续的列表项包装在ul中
    if (listItems.length > 0) {
      let listPattern = '';
      for (let i = 0; i < listItems.length; i++) {
        listPattern += `__LIST_ITEM_${i}__\n?`;
      }
      const listRegex = new RegExp(listPattern.replace(/\n\?$/, ''), 'g');
      processedContent = processedContent.replace(listRegex, `<ul>${listItems.join('')}</ul>`);
    }

    // 处理有序列表
    const orderedListItems = [];
    processedContent = processedContent.replace(/^[\s]*\d+\. (.+)$/gm, (match, item) => {
      const placeholder = `__ORDERED_LIST_ITEM_${orderedListItems.length}__`;
      orderedListItems.push(`<li>${item}</li>`);
      return placeholder;
    });

    // 将连续的有序列表项包装在ol中
    if (orderedListItems.length > 0) {
      let orderedListPattern = '';
      for (let i = 0; i < orderedListItems.length; i++) {
        orderedListPattern += `__ORDERED_LIST_ITEM_${i}__\n?`;
      }
      const orderedListRegex = new RegExp(orderedListPattern.replace(/\n\?$/, ''), 'g');
      processedContent = processedContent.replace(
        orderedListRegex,
        `<ol>${orderedListItems.join('')}</ol>`,
      );
    }

    // 处理引用
    processedContent = processedContent.replace(/^> (.+)$/gm, '<blockquote><p>$1</p></blockquote>');

    // 处理段落：将双换行转换为段落分隔
    const lines = processedContent.split('\n');
    const paragraphs = [];
    let currentParagraph = '';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      // 如果是空行
      if (!line) {
        if (currentParagraph.trim()) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = '';
        }
        continue;
      }

      // 如果是HTML标签行或占位符，直接作为段落
      if (line.match(/^<(h[1-6]|pre|ul|ol|blockquote|div)/) || line.match(/^__\w+_\d+__$/)) {
        if (currentParagraph.trim()) {
          paragraphs.push(currentParagraph.trim());
          currentParagraph = '';
        }
        paragraphs.push(line);
        continue;
      }

      // 普通文本行
      if (currentParagraph) {
        currentParagraph += `<br>${line}`;
      } else {
        currentParagraph = line;
      }
    }

    // 处理最后一个段落
    if (currentParagraph.trim()) {
      paragraphs.push(currentParagraph.trim());
    }

    // 将段落包装在<p>标签中
    processedContent = paragraphs
      .map(paragraph => {
        if (
          paragraph.match(/^<(h[1-6]|pre|ul|ol|blockquote|div)/) ||
          paragraph.match(/^__\w+_\d+__$/)
        ) {
          return paragraph;
        }
        return `<p>${paragraph}</p>`;
      })
      .join('\n\n');

    // 恢复代码块
    codeBlocks.forEach((codeBlock, index) => {
      processedContent = processedContent.replace(`__CODE_BLOCK_${index}__`, codeBlock);
    });

    // 恢复行内代码
    inlineCodes.forEach((inlineCode, index) => {
      processedContent = processedContent.replace(`__INLINE_CODE_${index}__`, inlineCode);
    });

    return processedContent;
  };
  const calculateReadTime = content => {
    if (!content) {
      return 0;
    }

    // 移除HTML标签
    const textContent = content.replace(/<[^>]*>/g, '');

    // 计算中文字符数（中文、日文、韩文等）
    const chineseChars = (
      textContent.match(/[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3040-\u309f\u30a0-\u30ff]/g) ||
      []
    ).length;

    // 计算英文单词数
    const englishText = textContent.replace(
      /[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff\u3040-\u309f\u30a0-\u30ff]/g,
      '',
    );
    const englishWords = englishText.trim() ? englishText.trim().split(/\s+/).length : 0;

    // 中文阅读速度：300-500字/分钟，取400字/分钟
    // 英文阅读速度：200-250词/分钟，取225词/分钟
    const chineseReadingTime = chineseChars / 400;
    const englishReadingTime = englishWords / 225;

    const totalReadingTime = chineseReadingTime + englishReadingTime;

    // 最少1分钟，向上取整
    return Math.max(1, Math.ceil(totalReadingTime));
  };

  // 获取上一篇和下一篇文章
  const getAdjacentPosts = (currentPost, allPosts) => {
    if (!currentPost || !allPosts || allPosts.length === 0) {
      console.log('getAdjacentPosts: 缺少必要参数', {
        currentPost: !!currentPost,
        allPosts: allPosts?.length,
      });
      return { prevPost: null, nextPost: null };
    }

    const currentIndex = allPosts.findIndex(post => post.id === currentPost.id);
    console.log('getAdjacentPosts: 当前文章索引', {
      currentPostId: currentPost.id,
      currentIndex,
      totalPosts: allPosts.length,
      allPostIds: allPosts.map(p => p.id),
    });

    if (currentIndex === -1) {
      console.log('getAdjacentPosts: 在allPosts中未找到当前文章');
      return { prevPost: null, nextPost: null };
    }

    // 确保不会返回相同的文章
    const prevPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;
    const nextPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;

    console.log('getAdjacentPosts: 相邻文章', {
      prevPost: prevPost ? { id: prevPost.id, title: prevPost.title } : null,
      nextPost: nextPost ? { id: nextPost.id, title: nextPost.title } : null,
    });

    // 额外检查：确保相邻文章不是当前文章
    if (prevPost && prevPost.id === currentPost.id) {
      console.warn('getAdjacentPosts: prevPost与当前文章相同，设置为null');
      return { prevPost: null, nextPost };
    }
    if (nextPost && nextPost.id === currentPost.id) {
      console.warn('getAdjacentPosts: nextPost与当前文章相同，设置为null');
      return { prevPost, nextPost: null };
    }

    return { prevPost, nextPost };
  };

  // 检查URL参数中是否有postId
  useEffect(() => {
    // 将复制代码块函数暴露到全局作用域
    window.copyCodeBlock = copyCodeBlock;

    const urlParams = new URLSearchParams(location.search);
    const postId = urlParams.get('postId');

    if (postId) {
      // 如果URL中有postId，直接获取文章详情
      const loadPostDetail = async () => {
        try {
          const data = await fetchPostDetail(postId);
          // eslint-disable-next-line no-console
          console.log('Post detail received:', data);

          // 根据API文档，文章详情的响应格式是 {success: true, data: {...}}
          let postData;
          if (data && data.success && data.data) {
            postData = data.data;
          } else if (data && !data.success) {
            // eslint-disable-next-line no-console
            console.error('API returned error:', data);
            setSelectedPost(null);
            navigate('/blog', { replace: true });
            return;
          } else {
            // 兼容直接返回文章对象的情况
            postData = data;
          }

          // 生成目录并更新内容
          const { toc, updatedContent } = generateTableOfContents(postData.content);
          setTableOfContents(toc);

          // 一次性设置完整的文章数据，包含更新后的内容
          setSelectedPost({ ...postData, content: updatedContent });
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Error loading post detail from URL:', err);
          setSelectedPost(null);
          // 如果加载失败，清除URL参数
          navigate('/blog', { replace: true });
        }
      };
      loadPostDetail();
    }

    // 清理函数
    return () => {
      delete window.copyCodeBlock;
    };
  }, [location.search]); // 移除copyCodeBlock依赖项，防止无限循环

  // 加载所有文章用于左侧列表
  useEffect(() => {
    const loadAllPosts = async () => {
      try {
        // 获取前100篇文章用于左侧列表
        const data = await fetchPosts(1, 100);
        if (data && data.success && data.data && Array.isArray(data.data.posts)) {
          // 按时间倒序排列
          const sortedPosts = data.data.posts.sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at),
          );
          setAllPosts(sortedPosts);
        } else if (Array.isArray(data)) {
          const sortedPosts = data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          setAllPosts(sortedPosts);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error loading all posts:', err);
      }
    };

    loadAllPosts();
  }, []);

  // 加载博客列表
  useEffect(() => {
    const loadPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        // 获取当前页的博客数据
        const data = await fetchPosts(currentPage, POSTS_PER_PAGE);
        // eslint-disable-next-line no-console
        console.log('Blog page received data:', data);

        // 根据API文档，正确的响应格式是 {success: true, data: {posts: [...], pagination: {...}}}
        if (data && data.success && data.data && Array.isArray(data.data.posts)) {
          setPosts(data.data.posts);

          // 设置分页信息
          if (data.data.pagination) {
            setTotalPages(data.data.pagination.total_pages);
          } else {
            setTotalPages(Math.ceil(data.data.posts.length / POSTS_PER_PAGE));
          }
        } else if (Array.isArray(data)) {
          // 兼容直接返回数组的情况
          setPosts(data);
          setTotalPages(Math.ceil(data.length / POSTS_PER_PAGE));
        } else if (data && data.posts) {
          // 兼容直接返回包含posts属性的对象
          setPosts(data.posts);
          setTotalPages(Math.ceil((data.total || data.posts.length) / POSTS_PER_PAGE));
        } else {
          // eslint-disable-next-line no-console
          console.warn('Unexpected data format:', data);
          setPosts([]);
          setTotalPages(1);
        }
      } catch (err) {
        setError('Failed to load blog posts');
        // eslint-disable-next-line no-console
        console.error('Error loading posts:', err);
        setPosts([]);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, [currentPage]);

  // 当posts更新时，设置特色文章
  useEffect(() => {
    if (currentPage === 1 && posts.length > 0 && !featuredPost) {
      setFeaturedPost(posts[0]);
    }
  }, [posts, currentPage, featuredPost]);

  // ScrollReveal 动画
  useEffect(() => {
    if (prefersReducedMotion || !sr) {
      return;
    }

    if (revealTitle.current && sr) {
      sr.reveal(revealTitle.current, srConfig());
    }
    if (revealSocial.current && sr) {
      sr.reveal(revealSocial.current, srConfig(100));
    }
    if (revealFeatured.current && sr) {
      sr.reveal(revealFeatured.current, srConfig(200));
    }
    if (revealGrid.current && sr) {
      sr.reveal(revealGrid.current, srConfig(300));
    }
    if (revealPagination.current && sr) {
      sr.reveal(revealPagination.current, srConfig(400));
    }
  }, [prefersReducedMotion]);

  // 监听滚动，高亮当前标题
  useEffect(() => {
    if (!selectedPost || tableOfContents.length === 0) {
      return;
    }

    const handleScroll = () => {
      const headings = tableOfContents
        .map(item => document.getElementById(item.id))
        .filter(Boolean);

      let currentHeading = '';
      for (let i = headings.length - 1; i >= 0; i--) {
        const heading = headings[i];
        if (heading.getBoundingClientRect().top <= 100) {
          currentHeading = heading.id;
          break;
        }
      }

      setActiveHeading(currentHeading);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [selectedPost, tableOfContents]);

  // 添加全局锁防止并发调用
  const [isHandlingPostClick, setIsHandlingPostClick] = useState(false);

  /**
   * 处理文章点击事件
   * @param {Object} post - 文章数据
   */
  const handlePostClick = async post => {
    console.log('handlePostClick: 点击文章', {
      postId: post.id,
      currentPostId: selectedPost?.id,
      isLoading: !!loadingPostId,
      isHandling: isHandlingPostClick,
    });

    // 如果正在处理其他点击事件，直接返回
    if (isHandlingPostClick) {
      console.log('handlePostClick: 正在处理其他点击事件，跳过');
      return;
    }

    // 如果正在加载同一篇文章，直接返回
    if (loadingPostId === post.id) {
      console.log('handlePostClick: 正在加载同一篇文章，跳过');
      return;
    }

    // 如果点击的是当前已显示的文章，直接返回
    if (selectedPost && selectedPost.id === post.id) {
      console.log('handlePostClick: 点击的是当前文章，跳过');
      return;
    }

    // 设置处理锁
    setIsHandlingPostClick(true);

    // 清除之前的防抖定时器
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // 设置防抖定时器
    const timer = setTimeout(async () => {
      try {
        setLoadingPostId(post.id);

        const data = await fetchPostDetail(post.id);
        // eslint-disable-next-line no-console
        console.log('Post detail received:', data);

        // 根据API文档，文章详情的响应格式是 {success: true, data: {...}}
        let postData;
        if (data && data.success && data.data) {
          postData = data.data;
        } else if (data && !data.success) {
          // eslint-disable-next-line no-console
          console.error('API returned error:', data);
          return;
        } else {
          // 兼容直接返回文章对象的情况
          postData = data;
        }

        // 生成目录并更新内容
        const { toc, updatedContent } = generateTableOfContents(postData.content);
        setTableOfContents(toc);

        // 一次性设置完整的文章数据，包含更新后的内容
        setSelectedPost({ ...postData, content: updatedContent });

        // 更新 URL 但不刷新页面
        navigate(`/blog?postId=${post.id}`, { replace: true });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching post detail:', err);
      } finally {
        setLoadingPostId(null);
        setIsHandlingPostClick(false); // 释放处理锁
      }
    }, 200); // 200ms 防抖延迟

    setDebounceTimer(timer);
  };

  /**
   * 关闭文章详情
   */
  const handleCloseDetail = () => {
    setSelectedPost(null);
    setTableOfContents([]);
    setActiveHeading('');
    navigate('/blog', { replace: true });
  };

  /**
   * 处理分页
   */
  const handlePageChange = page => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /**
   * 获取显示的博客列表（排除特色文章）
   */
  const getDisplayPosts = () => {
    if (currentPage === 1 && posts.length > 1) {
      // 第一页排除第一篇文章（特色文章）
      return posts.slice(1);
    }
    return posts;
  };

  /**
   * 处理目录点击
   */
  const handleTocClick = id => {
    const scrollToElement = () => {
      const element = document.getElementById(id);
      if (element) {
        // 计算偏移量，考虑固定头部的高度
        const headerOffset = 80; // 根据实际头部高度调整
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });

        // 更新活动标题
        setActiveHeading(id);
        return true;
      }
      return false;
    };

    // 立即尝试滚动
    if (!scrollToElement()) {
      // 如果元素不存在，等待一小段时间后重试（可能是内容还在渲染）
      setTimeout(() => {
        if (!scrollToElement()) {
          console.warn(`无法找到ID为 "${id}" 的标题元素`);
        }
      }, 100);
    }
  };

  // 清理防抖定时器
  useEffect(
    () => () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    },
    [debounceTimer],
  );
  useEffect(() => {
    // 只有当文章启用广告且有有效广告代码时才执行
    if (
      selectedPost &&
      selectedPost.enable_ads &&
      selectedPost.ads_code &&
      selectedPost.ads_code.trim()
    ) {
      // 延迟执行广告代码，确保DOM已经渲染完成
      const timer = setTimeout(() => {
        try {
          // 如果是Google AdSense代码，确保adsbygoogle数组存在并推送
          if (selectedPost.ads_code.includes('adsbygoogle')) {
            if (typeof window !== 'undefined') {
              // 确保adsbygoogle数组存在
              window.adsbygoogle = window.adsbygoogle || [];

              // 清理之前的广告位，避免重复
              const existingAds = document.querySelectorAll(
                '.ad-content ins.adsbygoogle[data-adsbygoogle-status]',
              );
              existingAds.forEach(ad => {
                // 移除已处理的标记，让新的广告位可以正常加载
                ad.removeAttribute('data-adsbygoogle-status');
                ad.removeAttribute('data-ad-status');
              });

              // 查找新插入的广告位
              const adElements = document.querySelectorAll('.ad-content ins.adsbygoogle');
              adElements.forEach(ad => {
                // 只处理未标记的广告位
                if (!ad.dataset.adsbygoogleStatus && !ad.dataset.adStatus) {
                  try {
                    window.adsbygoogle.push({});
                    ad.dataset.adsbygoogleStatus = 'done';
                  } catch (e) {
                    console.warn('AdSense推送失败:', e);
                  }
                }
              });
            }
          }

          // 执行其他可能的广告脚本
          const scripts = document.querySelectorAll('.ad-content script');
          scripts.forEach(script => {
            if (script.innerHTML && !script.src && !script.dataset.executed) {
              try {
                // 标记脚本已执行，避免重复执行
                script.dataset.executed = 'true';
                // eslint-disable-next-line no-eval
                eval(script.innerHTML);
              } catch (e) {
                console.warn('广告脚本执行失败:', e);
              }
            }
          });
        } catch (e) {
          console.warn('广告初始化失败:', e);
        }
      }, 300);

      return () => {
        clearTimeout(timer);
        // 清理函数：移除当前文章的广告脚本标记
        const scripts = document.querySelectorAll('.ad-content script[data-executed]');
        scripts.forEach(script => {
          script.removeAttribute('data-executed');
        });
      };
    }
  }, [selectedPost]);

  /**
   * 解析打赏二维码数据
   */
  const parseRewardQRCode = useCallback(() => {
    if (!selectedPost?.reward_qr_code) {return null;}

    try {
      // 尝试解析JSON格式的reward_qr_code
      if (
        typeof selectedPost.reward_qr_code === 'string' &&
        selectedPost.reward_qr_code.startsWith('{')
      ) {
        const rewardData = JSON.parse(selectedPost.reward_qr_code);
        return {
          qr_code: rewardData.qr_code,
          title: rewardData.title || t('supportAuthor'),
          description: rewardData.description || t('scanToDonate'),
        };
      }

      // 兼容旧格式：直接是URL字符串
      return {
        qr_code: selectedPost.reward_qr_code,
        title: t('supportAuthor'),
        description: t('scanToDonate'),
      };
    } catch (error) {
      console.warn('解析reward_qr_code失败:', error);
      return null;
    }
  }, [selectedPost?.reward_qr_code, t]);

  /**
   * 渲染广告内容
   */
  const renderAdContent = () => {
    // 只有当文章明确启用广告且有广告代码时才渲染
    if (
      selectedPost &&
      selectedPost.enable_ads &&
      selectedPost.ads_code &&
      selectedPost.ads_code.trim()
    ) {
      return (
        <div className="ad-content" dangerouslySetInnerHTML={{ __html: selectedPost.ads_code }} />
      );
    }

    // 如果没有广告代码，返回null
    return null;
  };

  /**
   * 检查是否应该显示广告区域
   */
  const shouldShowAds = () =>
    // 只有当文章明确启用广告且有有效的广告代码时才显示广告区域
    !!(
      selectedPost &&
      selectedPost.enable_ads &&
      selectedPost.ads_code &&
      selectedPost.ads_code.trim() &&
      selectedPost.ads_code.trim().length > 0
    );
  if (loading) {
    return (
      <Layout location={location}>
        <Helmet
          title={`${t('MyBlogTitle')} | Product Management Insights & Web3 Technology Blog`}
          meta={[
            {
              name: 'description',
              content:
                'Expert insights on product management, Web3 technology, blockchain development, cryptocurrency trends, fintech innovation, and digital transformation strategies. Learn from real-world case studies and industry best practices.',
            },
            {
              name: 'keywords',
              content:
                'product management blog, web3 technology blog, blockchain development insights, cryptocurrency trends, fintech innovation, digital transformation, product strategy, user experience design, startup insights, tech industry analysis, product management best practices, agile development, product roadmap, market research, competitive analysis, product metrics, growth hacking, product-market fit, venture capital insights, tech entrepreneurship',
            },
            {
              property: 'og:title',
              content: `${t('MyBlogTitle')} | Product Management & Web3 Technology Blog`,
            },
            {
              property: 'og:description',
              content:
                'Expert insights on product management, Web3 technology, blockchain development, and fintech innovation. Real-world case studies and industry best practices.',
            },
            {
              property: 'og:type',
              content: 'website',
            },
            {
              property: 'og:url',
              content: typeof window !== 'undefined' ? window.location.href : '',
            },
            {
              property: 'og:image',
              content: '/og-image.png',
            },
            {
              name: 'twitter:card',
              content: 'summary_large_image',
            },
            {
              name: 'twitter:title',
              content: `${t('MyBlogTitle')} | Product Management & Web3 Blog`,
            },
            {
              name: 'twitter:description',
              content:
                'Expert insights on product management, Web3 technology, blockchain development, and fintech innovation.',
            },
            {
              name: 'twitter:image',
              content: '/og-image.png',
            },
            {
              name: 'author',
              content: 'Y Lau',
            },
            {
              name: 'robots',
              content: 'index, follow',
            },
          ]}
        />
        <StyledLoadingContainer>
          <div className="loading-text">{t('loadingPosts')}</div>
        </StyledLoadingContainer>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout location={location}>
        <Helmet
          title={`${t('MyBlogTitle')} | Product Management Insights & Web3 Technology Blog`}
          meta={[
            {
              name: 'description',
              content:
                'Expert insights on product management, Web3 technology, blockchain development, cryptocurrency trends, fintech innovation, and digital transformation strategies. Learn from real-world case studies and industry best practices.',
            },
            {
              name: 'keywords',
              content:
                'product management blog, web3 technology blog, blockchain development insights, cryptocurrency trends, fintech innovation, digital transformation, product strategy, user experience design, startup insights, tech industry analysis, product management best practices, agile development, product roadmap, market research, competitive analysis, product metrics, growth hacking, product-market fit, venture capital insights, tech entrepreneurship',
            },
            {
              property: 'og:title',
              content: `${t('MyBlogTitle')} | Product Management & Web3 Technology Blog`,
            },
            {
              property: 'og:description',
              content:
                'Expert insights on product management, Web3 technology, blockchain development, and fintech innovation. Real-world case studies and industry best practices.',
            },
            {
              property: 'og:type',
              content: 'website',
            },
            {
              property: 'og:url',
              content: typeof window !== 'undefined' ? window.location.href : '',
            },
            {
              property: 'og:image',
              content: '/og-image.png',
            },
            {
              name: 'twitter:card',
              content: 'summary_large_image',
            },
            {
              name: 'twitter:title',
              content: `${t('MyBlogTitle')} | Product Management & Web3 Blog`,
            },
            {
              name: 'twitter:description',
              content:
                'Expert insights on product management, Web3 technology, blockchain development, and fintech innovation.',
            },
            {
              name: 'twitter:image',
              content: '/og-image.png',
            },
            {
              name: 'author',
              content: 'Y Lau',
            },
            {
              name: 'robots',
              content: 'index, follow',
            },
          ]}
        />
        <StyledErrorContainer>
          <div className="error-text">
            {t('errorLoadingPosts')}: {error}
          </div>
        </StyledErrorContainer>
      </Layout>
    );
  }

  // 如果有选中的文章，显示文章详情页面（三栏布局）
  if (selectedPost) {
    return (
      <Layout location={location}>
        <Helmet
          title={selectedPost.title}
          meta={[
            {
              name: 'description',
              content:
                selectedPost.description ||
                selectedPost.content?.replace(/<[^>]*>/g, '').substring(0, 160) ||
                selectedPost.title,
            },
            {
              name: 'keywords',
              content: `${
                selectedPost.tags || 'product management, web3, blockchain, fintech'
              }, product management insights, web3 technology, blockchain development, cryptocurrency trends, fintech innovation, digital transformation, startup strategies, tech industry analysis, product strategy, user experience design`,
            },
            {
              property: 'og:title',
              content: selectedPost.title,
            },
            {
              property: 'og:description',
              content:
                selectedPost.description ||
                selectedPost.content?.replace(/<[^>]*>/g, '').substring(0, 160) ||
                selectedPost.title,
            },
            {
              property: 'og:type',
              content: 'article',
            },
            {
              property: 'og:url',
              content: typeof window !== 'undefined' ? window.location.href : '',
            },
            {
              property: 'og:image',
              content: selectedPost.cover_image || '/og-image.png',
            },
            {
              property: 'article:published_time',
              content: selectedPost.created_at,
            },
            {
              property: 'article:author',
              content: 'Y Lau',
            },
            {
              property: 'article:tag',
              content: selectedPost.tags,
            },
            {
              name: 'twitter:card',
              content: 'summary_large_image',
            },
            {
              name: 'twitter:title',
              content: selectedPost.title,
            },
            {
              name: 'twitter:description',
              content:
                selectedPost.description ||
                selectedPost.content?.replace(/<[^>]*>/g, '').substring(0, 160) ||
                selectedPost.title,
            },
            {
              name: 'twitter:image',
              content: selectedPost.cover_image || '/og-image.png',
            },
          ]}
        />
        <StyledDetailContainer>
          {/* 左侧文章列表 */}
          <StyledArticleList>
            <div className="list-header">
              <StyledBackButton onClick={handleCloseDetail}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
                {t('backToBlogList')}
              </StyledBackButton>
              <h3>{t('allArticles')}</h3>
            </div>
            <div className="article-items">
              {allPosts.map(post => (
                <div
                  key={post.id}
                  className={`article-item ${post.id === selectedPost.id ? 'active' : ''} ${
                    loadingPostId === post.id ? 'loading' : ''
                  }`}
                  onClick={() => handlePostClick(post)}>
                  <div className="article-date">
                    {formatDate(post.created_at, getLocaleFromLanguage(currentLanguage))}
                  </div>
                  <div className="article-title">{post.title}</div>
                </div>
              ))}
            </div>
          </StyledArticleList>

          {/* 中间文章内容 */}
          <StyledArticleContent>
            <StyledArticleHeader>
              {/* 移动端文章头部返回按钮 */}
              <StyledArticleBackButton onClick={handleCloseDetail}>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
                {t('backToBlogList')}
              </StyledArticleBackButton>

              <h1 className="article-title">{selectedPost.title}</h1>
              <div className="article-meta">
                <span className="publish-date">
                  {t('publishedOn')}{' '}
                  {formatDate(selectedPost.created_at, getLocaleFromLanguage(currentLanguage))}
                </span>
                <span className="read-time">
                  {t('readTime')}: {calculateReadTime(selectedPost.content)} {t('minutes')}
                </span>
              </div>
              {selectedPost.tags && (
                <div className="article-tags">
                  {parseTags(selectedPost.tags).map((tag, i) => (
                    <span key={i} className="tag">
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* 社交媒体分享按钮 */}
              <StyledSocialShare>
                <span className="share-label">{t('shareArticle')}:</span>
                <div className="share-buttons">
                  {/* 系统原生分享按钮 */}
                  <button
                    className="share-button native"
                    onClick={() => shareNative(selectedPost.title, window.location.href)}
                    title={t('systemShare')}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" />
                    </svg>
                  </button>
                  <button
                    className="share-button twitter"
                    onClick={() => shareToTwitter(selectedPost, window.location.href)}
                    title={t('shareToTwitter')}>
                    <XIcon size={18} />
                  </button>
                  <button
                    className="share-button telegram"
                    onClick={() => shareToTelegram(selectedPost, window.location.href)}
                    title={t('shareToTelegram')}>
                    <TelegramIcon size={18} />
                  </button>
                  <button
                    className="share-button wechat"
                    onClick={() => shareToWechat(selectedPost, window.location.href)}
                    title={t('shareToWeChat')}>
                    <WeixinIcon size={18} />
                  </button>
                  <button
                    className="share-button facebook"
                    onClick={() => shareToFacebook(selectedPost, window.location.href)}
                    title="分享到 Facebook">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </button>
                  <button
                    className="share-button linkedin"
                    onClick={() => shareToLinkedIn(selectedPost, window.location.href)}
                    title="分享到 LinkedIn">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  </button>
                  <button
                    className="share-button whatsapp"
                    onClick={() => shareToWhatsApp(selectedPost, window.location.href)}
                    title="分享到 WhatsApp">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.89 3.488" />
                    </svg>
                  </button>
                  <button
                    className="share-button copy"
                    onClick={() => copyToClipboard(window.location.href)}
                    title={t('copyLink')}>
                    <CopyIcon size={18} />
                  </button>
                </div>
              </StyledSocialShare>
            </StyledArticleHeader>

            {/* 移动端目录 - 放在文章内容前面 */}
            {tableOfContents.length > 0 ? (
              <StyledMobileTableOfContents className="mobile-toc">
                <h4>{t('tableOfContents')}</h4>
                <ul className="toc-list">
                  {tableOfContents.map(item => (
                    <li
                      key={item.id}
                      className={`toc-item level-${item.level} ${
                        activeHeading === item.id ? 'active' : ''
                      }`}
                      onClick={() => handleTocClick(item.id)}>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </StyledMobileTableOfContents>
            ) : null}

            <StyledArticleBody>
              {selectedPost.cover_image && (
                <div className="cover-image">
                  <img src={selectedPost.cover_image} alt={selectedPost.title} />
                </div>
              )}
              <div
                className="article-content"
                dangerouslySetInnerHTML={{ __html: processArticleContent(selectedPost.content) }}
              />
            </StyledArticleBody>

            {/* 文章导航 - 上一篇/下一篇 */}
            {(() => {
              const { prevPost, nextPost } = getAdjacentPosts(selectedPost, allPosts);
              if (prevPost || nextPost) {
                return (
                  <StyledArticleNavigation>
                    {prevPost && (
                      <StyledNavButton
                        className="prev"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (prevPost.id !== selectedPost.id) {
                            handlePostClick(prevPost);
                          }
                        }}>
                        <svg className="nav-arrow" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                        </svg>
                        <div className="nav-content">
                          <span className="nav-label">{t('previousArticle')}</span>
                          <span className="nav-title">{prevPost.title}</span>
                        </div>
                      </StyledNavButton>
                    )}
                    {nextPost && (
                      <StyledNavButton
                        className="next"
                        onClick={e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (nextPost.id !== selectedPost.id) {
                            handlePostClick(nextPost);
                          }
                        }}>
                        <div className="nav-content">
                          <span className="nav-label">{t('nextArticle')}</span>
                          <span className="nav-title">{nextPost.title}</span>
                        </div>
                        <svg className="nav-arrow" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M4 11v2h12.17l-5.59 5.59L12 20l8-8-8-8-1.41 1.41L16.17 11H4z" />
                        </svg>
                      </StyledNavButton>
                    )}
                  </StyledArticleNavigation>
                );
              }
              return null;
            })()}

            {/* 打赏码部分 */}
            {(() => {
              const rewardData = parseRewardQRCode();
              return (
                selectedPost.enable_reward &&
                rewardData && (
                  <StyledDonationSection>
                    <h3>{rewardData.title}</h3>
                    <div className="donation-content">
                      <img src={rewardData.qr_code} alt={t('donationQRCode')} />
                      <p>{rewardData.description}</p>
                    </div>
                  </StyledDonationSection>
                )
              );
            })()}
          </StyledArticleContent>

          {/* 右侧边栏 */}
          <StyledSidebar>
            {/* 桌面端目录 */}
            {tableOfContents.length > 0 ? (
              <StyledTableOfContents className="desktop-toc">
                <h4>{t('tableOfContents')}</h4>
                <ul className="toc-list">
                  {tableOfContents.map(item => (
                    <li
                      key={item.id}
                      className={`toc-item level-${item.level} ${
                        activeHeading === item.id ? 'active' : ''
                      }`}
                      onClick={() => handleTocClick(item.id)}>
                      {item.text}
                    </li>
                  ))}
                </ul>
              </StyledTableOfContents>
            ) : null}

            {/* 广告区域 - 只有在有广告代码时才显示 */}
            {shouldShowAds() && (
              <StyledAdSection hasTableOfContents={tableOfContents.length > 0}>
                <h4>{t('advertisement')}</h4>
                <div className="ad-content">{renderAdContent()}</div>
              </StyledAdSection>
            )}
          </StyledSidebar>
        </StyledDetailContainer>
      </Layout>
    );
  }

  return (
    <Layout location={location}>
      <Helmet
        title={t('MyBlogTitle')}
        meta={[
          {
            name: 'description',
            content: t('MyBlogSubtitle') || 'Thoughts, stories and ideas from Y Lau',
          },
          {
            name: 'keywords',
            content: 'blog, tech, web development, programming, thoughts, stories',
          },
          {
            property: 'og:title',
            content: t('MyBlogTitle'),
          },
          {
            property: 'og:description',
            content: t('MyBlogSubtitle') || 'Thoughts, stories and ideas from Y Lau',
          },
          {
            property: 'og:type',
            content: 'website',
          },
          {
            property: 'og:url',
            content: typeof window !== 'undefined' ? window.location.href : '',
          },
          {
            property: 'og:image',
            content: '/og-image.png',
          },
          {
            name: 'twitter:card',
            content: 'summary_large_image',
          },
          {
            name: 'twitter:title',
            content: t('MyBlogTitle'),
          },
          {
            name: 'twitter:description',
            content: t('MyBlogSubtitle') || 'Thoughts, stories and ideas from Y Lau',
          },
          {
            name: 'twitter:image',
            content: '/og-image.png',
          },
        ]}
      />

      <StyledBlogContainer>
        <main>
          <header ref={revealTitle}>
            <h1 className="big-heading">{t('MyBlogTitle')}</h1>
            <p className="subtitle">{t('MyBlogSubtitle')}</p>
          </header>

          {/* 社交媒体链接 */}
          <StyledSocialContainer ref={revealSocial}>
            <span className="social-label">{t('socialShare')}:</span>
            {socialMediaConfig.map(({ name, icon: IconComponent, url, color, onClick }) => (
              <StyledSocialLink
                key={name}
                href={onClick ? '#' : url}
                target={name === 'Email' ? '_self' : '_blank'}
                rel="noopener noreferrer"
                hoverColor={color}
                aria-label={name}
                onClick={
                  onClick
                    ? e => {
                      e.preventDefault();
                      onClick();
                    }
                    : undefined
                }>
                <IconComponent size={20} />
              </StyledSocialLink>
            ))}
          </StyledSocialContainer>

          {/* 微信二维码弹窗 */}
          {showWechatModal && (
            <StyledModal
              onClick={() => {
                setShowWechatModal(false);
                setCurrentShareUrl('');
                setCurrentShareTitle('');
              }}>
              <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button
                  className="close-button"
                  onClick={() => {
                    setShowWechatModal(false);
                    setCurrentShareUrl('');
                    setCurrentShareTitle('');
                  }}>
                  ×
                </button>
                {currentShareUrl ? (
                  // 文章分享模式
                  <>
                    <h3>分享文章到微信</h3>
                    <div className="share-qr-container">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                          currentShareUrl,
                        )}`}
                        alt="文章分享二维码"
                        className="qr-image"
                      />
                    </div>
                    <p className="share-title">{currentShareTitle}</p>
                    <p>使用微信扫描二维码分享文章</p>
                    <div className="share-actions">
                      <button
                        className="copy-url-btn"
                        onClick={() => copyToClipboard(currentShareUrl)}>
                        复制链接
                      </button>
                    </div>
                  </>
                ) : (
                  // 个人微信模式
                  <>
                    <h3>{t('wechatQRCode')}</h3>
                    <img src={WechatcodeImg} alt={t('wechatQRCode')} className="qr-image" />
                    <p>{t('scanToAddWeChat')}</p>
                  </>
                )}
              </div>
            </StyledModal>
          )}

          {/* 特色文章 */}
          {featuredPost && currentPage === 1 && (
            <StyledFeaturedPost ref={revealFeatured} onClick={() => handlePostClick(featuredPost)}>
              <div className="featured-content">
                <div className="featured-badge">{t('latestPost')}</div>
                <h2 className="featured-title">{featuredPost.title}</h2>
                <p className="featured-description">
                  {featuredPost.description || t('defaultDescription')}
                </p>
                <div className="featured-meta">
                  <span className="featured-date">
                    {t('publishedOn')}{' '}
                    {formatDate(featuredPost.created_at, getLocaleFromLanguage(currentLanguage))}
                  </span>
                  {featuredPost.tags && (
                    <div className="featured-tags">
                      {parseTags(featuredPost.tags)
                        .slice(0, 3)
                        .map((tag, i) => (
                          <span key={i}>{tag}</span>
                        ))}
                    </div>
                  )}
                </div>
              </div>
              {featuredPost.cover_image && (
                <div className="featured-image">
                  <img src={featuredPost.cover_image} alt={featuredPost.title} />
                </div>
              )}
            </StyledFeaturedPost>
          )}

          {/* 博客网格 */}
          <StyledBlogGrid ref={revealGrid}>
            {getDisplayPosts().map(post => (
              <BlogCard key={post.id} post={post} onReadNow={handlePostClick} />
            ))}
          </StyledBlogGrid>

          {/* 分页 */}
          {totalPages > 1 && (
            <StyledPagination ref={revealPagination}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}>
                {t('previousPage')}
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={currentPage === page ? 'active' : ''}>
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}>
                {t('nextPage')}
              </button>

              <div className="page-info">
                {t('page')} {currentPage} {t('of')} {totalPages}
              </div>
            </StyledPagination>
          )}
        </main>
      </StyledBlogContainer>
    </Layout>
  );
};

MyBlogPage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default MyBlogPage;
